import deburr from "lodash/deburr";

import { FileTypeSchema, FileTypeType } from "../types";

const yymmdd = () => {
  const d = new Date();
  const yy = String(d.getFullYear()).slice(2);
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");

  return `${yy}${mm}${dd}`;
};

type GetPathParams = {
  companyId?: string;
  quoteRequestId?: string;
};
const STORAGE_CONFIG: Record<
  Exclude<FileTypeType, "ONBOARDING">,
  { getPath: (params: GetPathParams) => string }
> = {
  PART: {
    getPath: (params: GetPathParams) => {
      return `${params.companyId || "MISSING_COMPANY_ID"}/parts/`;
    },
  },
  COMPANY_LOGO: {
    getPath: (params: GetPathParams) => {
      return `${params.companyId || "MISSING_COMPANY_ID"}/assets/logo/`;
    },
  },
  COMPANY_CGV: {
    getPath: (params: GetPathParams) => {
      return `${params.companyId || "MISSING_COMPANY_ID"}/assets/cgv/`;
    },
  },
  MATERIALS_PRICING: {
    getPath: (params: GetPathParams) => {
      const today = yymmdd();
      return `${params.companyId || "MISSING_COMPANY_ID"}/materials/${today}/`;
    },
  },
  MATERIALS: {
    getPath: (params: GetPathParams) => {
      const today = yymmdd();
      return `${params.companyId || "MISSING_COMPANY_ID"}/materials/${today}/`;
    },
  },
  QUOTE_PDF: {
    getPath: (params: GetPathParams) => {
      return `${params.companyId || "MISSING_COMPANY_ID"}/quotes/`;
    },
  },
  EMAIL_REQUEST_SPREADSHEET: {
    getPath: (params: GetPathParams) => {
      return `${params.companyId || "MISSING_COMPANY_ID"}/email_requests/${params.quoteRequestId || "MISSING_QUOTE_REQUEST_ID"}/`;
    },
  },
  EMAIL_REQUEST_CONTENT: {
    getPath: (params: GetPathParams) => {
      return `${params.companyId || "MISSING_COMPANY_ID"}/email_requests/${params.quoteRequestId || "MISSING_QUOTE_REQUEST_ID"}/`;
    },
  },
} as const;

const MAX_FILENAME_SEGMENT_LENGTH = 200;
const EXISTING_HASH_PREFIX = /^(?:[0-9a-f]{64}-)+/i;

export const stripExistingHashPrefix = (name: string): string =>
  name.replace(EXISTING_HASH_PREFIX, "");

const truncateName = (name: string, maxLength: number): string => {
  if (name.length <= maxLength) {
    return name;
  }
  const lastDotIndex = name.lastIndexOf(".");
  const extension = lastDotIndex > 0 ? name.slice(lastDotIndex) : "";
  const base = lastDotIndex > 0 ? name.slice(0, lastDotIndex) : name;
  const maxBaseLength = Math.max(maxLength - extension.length, 1);
  return `${base.slice(0, maxBaseLength)}${extension}`;
};

export const makeFileKey = ({
  name,
  fileHash,
  type,
  dangerousFolderPathOverride,
  companyId,
}: {
  name: string;
  fileHash: string | null;
  type: FileTypeType;
  dangerousFolderPathOverride?: string;
  companyId?: string;
}): string => {
  const deduplicatedName = stripExistingHashPrefix(name);
  const hashPrefix = fileHash ? `${fileHash}-` : "";
  const truncatedName = truncateName(
    deduplicatedName,
    MAX_FILENAME_SEGMENT_LENGTH - hashPrefix.length,
  );
  const hashWithName = `${hashPrefix}${truncatedName}`;
  const path =
    type === FileTypeSchema.enum.ONBOARDING
      ? dangerousFolderPathOverride
      : STORAGE_CONFIG[type].getPath({ companyId });
  if (!path) {
    return cleanUrl(hashWithName);
  }
  return cleanUrl(`${path}${hashWithName}`);
};

const S3_UNSAFE_CHAR = /[^0-9a-zA-Z!\-_.*'()/]/g;

const cleanUrl = (str: string) => {
  return deburr(str)
    .replace(/\s/g, "-")
    .replace(S3_UNSAFE_CHAR, "-")
    .split("/")
    .filter((segment) => segment && segment !== ".")
    .join("/");
};
